import React, { useCallback, useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Card,
  CircularProgress,
  Snackbar,
  Stack,
  TextField,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import { getHooterConfig, updateHooterConfig, testHooterPlayback } from '../utils/api';
import { formatDate } from '../utils/dateUtils';
import PageHeader from './PageHeader';

const HooterConfiguration = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [error, setError] = useState('');
  const [host, setHost] = useState('');
  const [alarmDurationSeconds, setAlarmDurationSeconds] = useState('5');
  const [savedForm, setSavedForm] = useState({ host: '', alarmDurationSeconds: '5' });
  const [updatedBy, setUpdatedBy] = useState('');
  const [updatedAt, setUpdatedAt] = useState('');
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

  const applyForm = (hostValue, durationValue) => {
    setHost(String(hostValue || ''));
    setAlarmDurationSeconds(String(durationValue ?? 5));
  };

  const enterViewMode = (hostValue, durationValue) => {
    const nextHost = String(hostValue || '');
    const nextDuration = String(durationValue ?? 5);
    applyForm(nextHost, nextDuration);
    setSavedForm({ host: nextHost, alarmDurationSeconds: nextDuration });
    setIsEditing(false);
  };

  const showSnackbar = (message, severity = 'success') => {
    setSnackbar({ open: true, message, severity });
  };

  const loadConfig = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const res = await getHooterConfig();
      const cfg = res?.config || {};
      const loadedHost = String(cfg.host || '');
      const loadedDuration = String(cfg.alarm_duration_seconds ?? 5);
      enterViewMode(loadedHost, loadedDuration);
      setUpdatedBy(String(cfg.updated_by || ''));
      setUpdatedAt(cfg.updated_at || '');
      setIsEditing(!loadedHost.trim());
    } catch (e) {
      setError(e.message || 'Failed to load hooter configuration');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadConfig();
  }, [loadConfig]);

  const handleSave = async () => {
    const hostTrim = host.trim();
    if (!hostTrim) {
      showSnackbar('Hooter IP is required', 'warning');
      return;
    }
    const duration = Number(alarmDurationSeconds);
    if (!Number.isFinite(duration) || duration < 1) {
      showSnackbar('Alarm duration must be at least 1 second', 'warning');
      return;
    }
    setSaving(true);
    setError('');
    try {
      const res = await updateHooterConfig({
        host: hostTrim,
        alarm_duration_seconds: Math.min(600, Math.round(duration)),
      });
      const cfg = res?.config || {};
      const savedHost = String(cfg.host || hostTrim);
      const savedDuration = String(cfg.alarm_duration_seconds ?? duration);
      enterViewMode(savedHost, savedDuration);
      setUpdatedBy(String(cfg.updated_by || ''));
      setUpdatedAt(cfg.updated_at || '');
      showSnackbar('Hooter configuration saved');
    } catch (e) {
      const msg = e.message || 'Save failed';
      setError(msg);
      showSnackbar(msg, 'error');
    } finally {
      setSaving(false);
    }
  };

  const handleCancel = () => {
    applyForm(savedForm.host, savedForm.alarmDurationSeconds);
    setIsEditing(false);
    setError('');
  };

  const handleTest = async () => {
    const hostTrim = host.trim();
    if (!hostTrim) {
      showSnackbar('Enter hooter IP before testing', 'warning');
      return;
    }
    setTesting(true);
    setError('');
    try {
      await testHooterPlayback(hostTrim);
      showSnackbar('Hooter test completed (ON for 1 second, then OFF)');
    } catch (e) {
      const msg = e.message || 'Hooter test failed';
      setError(msg);
      showSnackbar(msg, 'error');
    } finally {
      setTesting(false);
    }
  };

  const readOnlyFieldProps = isEditing
    ? {}
    : {
        InputProps: { readOnly: true },
        sx: {
          '& .MuiInputBase-root': {
            bgcolor: 'action.hover',
          },
        },
      };

  return (
    <Box sx={{ width: '100%' }}>
      <PageHeader title="Hooter Configuration" subtitle={null} icon={null} />

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
          {error}
        </Alert>
      )}

      <Card variant="outlined" sx={{ borderRadius: 3, overflow: 'hidden' }}>
        <Box
          sx={{
            px: { xs: 2, sm: 3 },
            py: 2,
            borderBottom: '1px solid',
            borderColor: 'divider',
          }}
        >
          <Typography variant="h6" sx={{ fontWeight: 600, fontSize: { xs: '1rem', sm: '1.25rem' } }}>
            Hooter device
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
            Device is turned on via <strong>/HOOTER_ON</strong> and off via <strong>/HOOTER_OFF</strong>.
          </Typography>
        </Box>

        <Box sx={{ p: { xs: 2, sm: 3 } }}>
          {loading ? (
            <Stack direction="row" spacing={1} alignItems="center">
              <CircularProgress size={22} />
              <Typography variant="body2" color="text.secondary">Loading…</Typography>
            </Stack>
          ) : (
            <Stack spacing={2.5} maxWidth={520}>
              <TextField
                label="Hooter IP / host"
                placeholder="192.168.1.74"
                value={host}
                onChange={(e) => setHost(e.target.value)}
                fullWidth
                size="small"
                {...readOnlyFieldProps}
                InputProps={{
                  ...(readOnlyFieldProps.InputProps || {}),
                  sx: { fontFamily: 'monospace', ...(readOnlyFieldProps.InputProps?.sx || {}) },
                }}
              />
              <TextField
                label="Alarm duration (seconds)"
                type="number"
                value={alarmDurationSeconds}
                onChange={(e) => setAlarmDurationSeconds(e.target.value)}
                fullWidth
                size="small"
                inputProps={{ min: 1, max: 600, step: 1 }}
                helperText={isEditing ? 'How long the hooter stays ON when an alarm fires' : undefined}
                {...readOnlyFieldProps}
              />
              {(updatedBy || updatedAt) && (
                <Typography variant="caption" color="text.secondary">
                  Last updated{updatedBy ? ` by ${updatedBy}` : ''}
                  {updatedAt ? ` · ${formatDate(updatedAt)}` : ''}
                </Typography>
              )}
              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5}>
                {isEditing ? (
                  <>
                    <Button
                      variant="contained"
                      onClick={handleSave}
                      disabled={saving || testing}
                      fullWidth={isMobile}
                      sx={{
                        textTransform: 'none',
                        fontWeight: 700,
                        bgcolor: '#40518A',
                        '&:hover': { bgcolor: '#32406d' },
                      }}
                    >
                      {saving ? 'Saving…' : 'Save'}
                    </Button>
                    {savedForm.host.trim() && (
                      <Button
                        variant="outlined"
                        onClick={handleCancel}
                        disabled={saving || testing}
                        fullWidth={isMobile}
                        sx={{ textTransform: 'none', fontWeight: 600 }}
                      >
                        Cancel
                      </Button>
                    )}
                  </>
                ) : (
                  <Button
                    variant="contained"
                    onClick={() => setIsEditing(true)}
                    disabled={testing}
                    fullWidth={isMobile}
                    sx={{
                      textTransform: 'none',
                      fontWeight: 700,
                      bgcolor: '#40518A',
                      '&:hover': { bgcolor: '#32406d' },
                    }}
                  >
                    Edit
                  </Button>
                )}
                <Button
                  variant="outlined"
                  onClick={handleTest}
                  disabled={testing || saving || !host.trim()}
                  fullWidth={isMobile}
                  sx={{ textTransform: 'none', fontWeight: 600 }}
                >
                  {testing ? 'Testing…' : 'Test hooter'}
                </Button>
              </Stack>
            </Stack>
          )}
        </Box>
      </Card>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
        message={snackbar.message}
      />
    </Box>
  );
};

export default HooterConfiguration;
